/**
 * 積分エンジン（§4.2・§4.7・§4.10）。1分tickで3本の積分を並走させる。
 * 不確かな時は安全側（被曝を多めに見積もる側）へ倒す（§1.2）。
 */
import {
  F_POSE,
  GAP_SUN_ASSUME_MAX_S,
  INDOOR_ACC_DURATION_S,
  INDOOR_ACC_M,
  JOULES_PER_SED,
  TICK_SECONDS,
  UVI_TO_WM2,
} from "./constants.js";
import { evaluateAlerts, type AlertContext } from "./alerts.js";
import { solarPosition } from "./solar.js";
import { protectionFactor } from "./sunscreen.js";
import { OPEN_SKY_CELL, sector } from "./terrain.js";
import { fdUv } from "./wx.js";
import type {
  AlertEvent,
  Application,
  EngineState,
  GpsFix,
  Ms,
  TerrainCell,
  UserSettings,
  WxMinute,
} from "./types.js";

export function initialEngineState(): EngineState {
  return {
    sAct: 0,
    sBare: 0,
    sAdh: 0,
    minOutdoor: 0,
    minSun: 0,
    minUnknown: 0,
    pm25ExposureMin: 0,
    application: null,
    virtualApplication: null,
    lastFix: null,
    poorAccSince: null,
    gapSince: null,
    sunSince: null,
    cmfHistory: [],
    lastAlertAt: {},
    alertsToday: 0,
    alertsFollowed: 0,
    applicationsToday: 0,
  };
}

export interface TickInput {
  now: Ms;
  /** このtickの測位。欠測は null（§7.3の補完規則を適用） */
  fix: GpsFix | null;
  wx: WxMinute;
  /** 現在地の地形セル。タイル未取得は null → 開空フォールバック（安全側） */
  cell: TerrainCell | null;
  /** JSTの時（0..23）。アラート夜間停止用（呼び出し側でタイムゾーン解決） */
  hourJst: number;
}

export interface EngineConfig {
  settings: UserSettings;
  /** S_adh の仮想塗布に使うSPF表示値（§4.7、ユーザーの登録商品から） */
  adherenceSpfLabel: number;
}

export interface TickResult {
  state: EngineState;
  alerts: AlertEvent[];
  /** デバッグ・画面表示用の内訳 */
  detail: {
    indoor: boolean;
    visible: boolean;
    eSkin: number; // W/m²
    sedRatePerH: number; // S_act の増加率 [SED/h]
    directShare: number; // E_skin中の直達成分比 0..1
  };
}

/** 屋内・欠測判定（§7.3）。stを更新し判定結果を返す */
function classifyPosition(
  st: EngineState,
  fix: GpsFix | null,
  now: Ms,
): { indoor: boolean; assumeSun: boolean; unknown: boolean } {
  if (fix) {
    st.gapSince = null;
    st.lastFix = fix;
    if (fix.acc > INDOOR_ACC_M) {
      if (st.poorAccSince === null) st.poorAccSince = now;
      // GPS精度>35mが3分継続 → 屋内とみなし E=0
      return { indoor: now - st.poorAccSince >= INDOOR_ACC_DURATION_S * 1000, assumeSun: false, unknown: false };
    }
    st.poorAccSince = null;
    return { indoor: false, assumeSun: false, unknown: false };
  }
  // 欠測：屋外らしい区間は日向仮定（δsun=1）を上限10分まで（安全側）
  if (st.gapSince === null) st.gapSince = now;
  if (now - st.gapSince <= GAP_SUN_ASSUME_MAX_S * 1000) {
    return { indoor: false, assumeSun: true, unknown: false };
  }
  // 10分超は屋内扱いに落とし「計測できなかった時間」として表示（隠さない）
  return { indoor: true, assumeSun: false, unknown: true };
}

/** 1分tick（§4.10擬似コードの実装） */
export function tick(st: EngineState, input: TickInput, cfg: EngineConfig): TickResult {
  const { now, wx } = input;
  const pos = input.fix ?? st.lastFix;
  const cls = classifyPosition(st, input.fix, now);

  let visible = false;
  let eSkin = 0;
  let directShare = 0;

  if (pos && !cls.indoor) {
    const sun = solarPosition(now, pos);
    // 日向仮定の欠測補完は開空セル（遮蔽なし）で計算＝安全側
    const cell = cls.assumeSun ? OPEN_SKY_CELL : (input.cell ?? OPEN_SKY_CELL);
    const horizon = cell.horizonDeg[sector(sun.azDeg)] ?? 0;
    visible = cls.assumeSun ? sun.elevDeg > 0 : sun.elevDeg > horizon;

    const fd = fdUv(wx.fdBb);
    const eOpen = UVI_TO_WM2 * wx.uvi; // §4.2 E_open
    const eDir = eOpen * (1 - fd) * (visible ? 1 : 0);
    const eDif = eOpen * fd * cell.svf;
    eSkin = (eDir + eDif) * F_POSE; // 反射項 E_ref は第2段（§4.8のマージンで吸収）
    directShare = eDir + eDif > 0 ? eDir / (eDir + eDif) : 0;
  }

  // 3本の積分（§4.7）：同一ループ内で防御条件だけ変える
  const dt = TICK_SECONDS;
  const pAct = protectionFactor(st.application, now, wx);
  const pAdh = protectionFactor(st.virtualApplication ?? st.application, now, wx);
  const dAct = (eSkin / pAct) * dt / JOULES_PER_SED;
  st.sAct += dAct;
  st.sBare += (eSkin * dt) / JOULES_PER_SED;
  st.sAdh += (eSkin / pAdh) * dt / JOULES_PER_SED;

  // 滞在時間・欠測の記録
  if (cls.unknown) st.minUnknown += 1;
  if (!cls.indoor && pos) {
    st.minOutdoor += 1;
    if (visible) st.minSun += 1;
    // 大気汚染の曝露は屋外にいる間だけ数える（肌年齢モデルの説明変数）
    st.pm25ExposureMin += wx.pm25;
  }

  // SUN_IN 3分継続・CMF 10分窓の状態更新
  if (visible) {
    if (st.sunSince === null) st.sunSince = now;
  } else {
    st.sunSince = null;
  }
  st.cmfHistory.push([now, wx.cmf]);
  st.cmfHistory = st.cmfHistory.filter(([ts]) => now - ts <= 11 * 60 * 1000);

  // アラート評価（§9）
  const alertCtx: AlertContext = {
    now,
    visible,
    indoor: cls.indoor,
    wx,
    sedRatePerS: dAct / dt,
    settings: cfg.settings,
    hourJst: input.hourJst,
  };
  const alerts = evaluateAlerts(st, alertCtx);

  // T15/DECAY 発火 → その時刻に r=1.0 で塗ったと仮想（S_adh、§4.7）
  for (const a of alerts) {
    if (a.type === "T15" || a.type === "DECAY") {
      st.virtualApplication = {
        ts: a.ts,
        productId: "virtual",
        spfLabel: cfg.adherenceSpfLabel,
        amount: "full",
        parts: [],
        waterproof: false,
        swimFactor: 1,
      };
    }
  }

  return {
    state: st,
    alerts,
    detail: {
      indoor: cls.indoor,
      visible,
      eSkin,
      sedRatePerH: (dAct / dt) * 3600,
      directShare,
    },
  };
}

/**
 * 塗布記録（「塗った」タップ）。T15系の再計算は次tickで自動反映（§9.2）。
 * 直近30分以内の塗りどき系アラートへの応答なら alerts_followed に計上【初期値・要検討】。
 */
export function recordApplication(st: EngineState, app: Application): void {
  st.application = app;
  st.applicationsToday += 1;
  const FOLLOW_WINDOW_MS = 30 * 60 * 1000;
  for (const type of ["T15", "DECAY", "SCHED"] as const) {
    const t = st.lastAlertAt[type];
    if (t !== undefined && app.ts - t >= 0 && app.ts - t <= FOLLOW_WINDOW_MS) {
      st.alertsFollowed += 1;
      break;
    }
  }
}
